import React, { useState, useEffect, useCallback, useRef } from 'react';
import config from "../../../config";
import noimage from '../../../assets/images/Desktop placeholder.svg';
import styles from "./editandcreate.module.css";
import { format } from 'date-fns';

import {
  addCateringProviderMenuImage
} from "../../../Apiservices/Auth/servers/fetchProviderData";

// ==================== REUSABLE COMPONENTS ====================
import { CustomDropdown } from './Customdropdown';
import { ItemInputWithDropdown } from './ItemInputWithDropdown';
import { VegNonVegToggle } from './VegNonVegToggle';
import { CategoryDropdown } from './CategoryDropdown';
import { CalendarDropdown } from "./CalendarDropdown"
import { CuisineDropdown } from './Cuisinedropdown';
import cateringmenuicon from '../../../assets/images/catering.png';

// ==================== ITEM SELECTION MODAL COMPONENT ====================
const ItemSelectionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  activeTabId, 
  activeSubTabId, 
  activeTabName, 
  activeSubTabName,
  allAvailableItems,
  initialSelectedItems = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedItems, setTempSelectedItems] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setTempSelectedItems([...initialSelectedItems]);
      setSearchQuery('');
    }
  }, [isOpen, initialSelectedItems]);

  const getFilteredItems = useCallback(() => {
    let filtered = allAvailableItems;

    const selectedGroupId = (activeSubTabId && activeSubTabId !== 0) ? activeSubTabId : activeTabId;

    if (selectedGroupId && selectedGroupId !== 0) {
      filtered = filtered.filter(item => {
        const itemGroupId = item.itemGroupId || item.itemGroup?.id || 0;
        return itemGroupId === selectedGroupId;
      });
    }

    if (searchQuery && searchQuery.trim()) {
      filtered = filtered.filter(item =>
        (item.itemName || "").toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allAvailableItems, activeTabId, activeSubTabId, searchQuery]);

  const toggleItemSelection = useCallback((item) => {
    setTempSelectedItems(prev => {
      const existingIndex = prev.findIndex(i => i.id === item.id);
      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        return [...prev, {
          ...item,
          itemId: item.id,
          itemName: item.itemName,
          qty: 1,
          itemGroupId: activeTabId,
          itemSubGroupId: activeSubTabId,
          dietCategoryName: item.dietCategoryName || null,
        }];
      }
    });
  }, [activeTabId, activeSubTabId]);

  const updateTempItemQuantity = useCallback((item, newQty) => {
    setTempSelectedItems(prev =>
      prev.map(i =>
        i.id === item.id ? { ...i, qty: Math.max(1, newQty) } : i
      )
    );
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm(tempSelectedItems);
    onClose();
  }, [tempSelectedItems, onConfirm, onClose]);

  if (!isOpen) return null;

  const filteredItems = getFilteredItems();

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Select Items - {activeSubTabName || activeTabName}</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <i className="pi pi-times" />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.searchContainer}>
            <i className="pi pi-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              style={{ paddingLeft: '35px' }}
            />
          </div>

          <div className={styles.itemsListContainer}>
            {filteredItems.length === 0 ? (
              <div className={styles.noItemsFound}>
                <p>No items found matching your criteria</p>
              </div>
            ) : (
              filteredItems.map((item) => {
                const isSelected = tempSelectedItems.some(i => i.id === item.id);
                const selectedItem = tempSelectedItems.find(i => i.id === item.id);

                return (
                  <div key={item.id} className={`${styles.modalItemRow} ${isSelected ? styles.selected : ''}`}>
                    <div className={styles.modalItemInfo}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleItemSelection(item)}
                        className={styles.checkbox}
                      />

                      {item.dietCategoryName && (
                        <span className={`${styles.categoryIndicator} ${styles[item.dietCategoryName?.toLowerCase()]}`} />
                      )}

                      <div className={styles.modalItemDetails}>
                        <span className={styles.modalItemName}>{item.itemName}</span>
                        {item.itemGroupName && (
                          <span className={styles.modalItemGroup}>{item.itemGroupName}</span>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <div className={styles.modalStepperContainer}>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedItem && selectedItem.qty > 1) {
                              updateTempItemQuantity(selectedItem, selectedItem.qty - 1);
                            } else if (selectedItem && selectedItem.qty === 1) {
                              toggleItemSelection(item);
                            }
                          }}
                          className={styles.stepperButton}
                        >
                          -
                        </button>
                        <span className={styles.modalQuantity}>
                          {selectedItem?.qty || 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedItem) {
                              updateTempItemQuantity(selectedItem, selectedItem.qty + 1);
                            } else {
                              toggleItemSelection(item);
                            }
                          }}
                          className={styles.stepperButton}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.selectionSummary}>
            <span>{tempSelectedItems.length} item(s) selected</span>
          </div>
          <div className={styles.modalActions}>
            <button onClick={onClose} className={styles.cancelButton}>
              Cancel
            </button>
            <button onClick={handleConfirm} className={styles.submitButton}>
              Add Selected ({tempSelectedItems.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== SERVICE FORM COMPONENT ====================
const ServiceForm = React.memo(({
  service = null,
  onSubmit,
  onCancel,
  cuisines,
  itemsCategories = [],
  categories = [],
  types = [],
  includedItemsList = [],
  allMasterItems,
  sidessallMasterItems,
  isEditing = false,
  onToggleEdit,
  selectedcategory,
  deleteButton = null,
  mode,
  fetchedevents,
  isPackageMode = true
}) => {
  const isEditMode = !!service;
  const isCreateMode = mode === "create";
  const isViewMode = mode === "view";
  const isEditFormMode = mode === "edit";

  const [isFormEditable, setIsFormEditable] = useState(isEditing || isCreateMode || isEditFormMode);

  const [selectedIncludedItems, setSelectedIncludedItems] = useState([]);

  const [activeTabId, setActiveTabId] = useState(null);
  const [activeSubTabId, setActiveSubTabId] = useState(null);
  const [activeTabName, setActiveTabName] = useState('');
  const [activeSubTabName, setActiveSubTabName] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);
  const [tempSelectedItems, setTempSelectedItems] = useState([]);
  const [allAvailableItems, setAllAvailableItems] = useState([]);

  const [formValid, setFormValid] = useState(true);

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    item_id: service?.item_id || null,
    custom_combo_name: service?.custom_combo_name || '',
    meal_id: selectedcategory || '',
    description: service?.description || '',
    regular_price: service?.regular_price || '',
    discounted_price: service?.discounted_price || '',
    included_items: service?.included_items || [],
    gallery: service?.gallery || [],
    valid_until: service?.valid_until || '',
    event_ids: service?.event_ids || [],
  });

  const [showDiscountCalendar, setShowDiscountCalendar] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const itemswithparentlist = allMasterItems || [];

  const [events, setEvents] = useState(fetchedevents || []);
  const [eventSearch, setEventSearch] = useState('');
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);

  const [newImages, setNewImages] = useState([]);

  const initialFormDataRef = useRef(null);
  const changedFieldsRef = useRef(new Set());

  const tabs = itemsCategories || [];

  useEffect(() => {
    if (allMasterItems && allMasterItems.length > 0) {
      const items = [];
      allMasterItems.forEach(categoryData => {
        if (categoryData.items && categoryData.items.length > 0) {
          categoryData.items.forEach(item => {
            items.push({
              ...item,
              categoryName: categoryData.category?.name || 'Uncategorized',
              categoryId: categoryData.category?.id,
              itemGroup: item.itemGroup || null,
              itemGroupId: item.itemGroup?.id || null,
              itemGroupName: item.itemGroup?.group_name || null,
              itemSubGroupId: item.itemSubGroupId || 0,
              itemSubGroupName: item.itemSubGroupName || ""
            });
          });
        }
      });
      setAllAvailableItems(items);
    }
  }, [allMasterItems]);

  useEffect(() => {
    setIsFormEditable(isEditing || isCreateMode || isEditFormMode);
  }, [isEditing, isCreateMode, isEditFormMode]);

  const validateForm = useCallback(() => {
    const hasItems = selectedIncludedItems.length > 0;
    const hasPackageName = formData.custom_combo_name?.trim().length > 0;
    
    if (isPackageMode) {
      const hasPrice = formData.regular_price && formData.regular_price > 0;
      setFormValid(hasItems && hasPackageName && hasPrice);
    } else {
      setFormValid(hasItems && hasPackageName);
    }
  }, [selectedIncludedItems, formData.custom_combo_name, formData.regular_price, isPackageMode]);

  const openItemSelection = useCallback((tabId, subTabId = 0, tabName = '', subTabName = '') => {
    setActiveTabId(tabId);
    setActiveSubTabId(subTabId);
    setActiveTabName(tabName);
    setActiveSubTabName(subTabName);

    const existingItems = selectedIncludedItems.filter(
      item => item.itemGroupId === tabId && item.itemSubGroupId === subTabId
    );
    setTempSelectedItems([...existingItems]);
    setShowItemModal(true);
  }, [selectedIncludedItems]);

  const confirmItemSelection = useCallback((selectedItems) => {
    setSelectedIncludedItems(prev => {
      const filtered = prev.filter(item =>
        !(item.itemGroupId === activeTabId && item.itemSubGroupId === activeSubTabId)
      );
      return [...filtered, ...selectedItems];
    });
    validateForm();
  }, [activeTabId, activeSubTabId, validateForm]);

  const updateItemQuantity = useCallback((item, newQty) => {
    setSelectedIncludedItems(prev =>
      prev.map(i =>
        i === item ? { ...i, qty: Math.max(1, newQty) } : i
      )
    );
    validateForm();
  }, [validateForm]);

  const removeItem = useCallback((itemToRemove) => {
    setSelectedIncludedItems(prev => prev.filter(item => item !== itemToRemove));
    validateForm();
  }, [validateForm]);

  const buildIncludedItemsSection = (tab) => {
    const hasSubGroups = tab.subGroups && tab.subGroups.length > 0;

    if (hasSubGroups) {
      return (
        <div key={tab.id} className={styles.tabSection}>
          <div className={styles.tabHeader}>
            <h5 className={styles.tabTitle}>{tab.group_name}</h5>
          </div>
          <div className={styles.subGroupsContainer}>
            {tab.subGroups.map((subGroup) => {
              const itemsForSubTab = selectedIncludedItems.filter(
                item => item.itemGroupId === tab.id && item.itemSubGroupId === subGroup.id
              );

              return (
                <div key={subGroup.id} className={styles.subGroupCard}>
                  <div className={styles.subGroupHeader}>
                    <span className={styles.subGroupTitle}>{subGroup.group_name}</span>
                    <button
                      type="button"
                      onClick={() => openItemSelection(tab.id, subGroup.id, tab.group_name, subGroup.group_name)}
                      className={styles.addButton}
                    >
                      ADD
                    </button>
                  </div>

                  {itemsForSubTab.length > 0 && <div className={styles.divider} />}

                  {itemsForSubTab.map((item, idx) => (
                    <div key={idx} className={styles.selectedItemRow}>
                      <div className={styles.itemInfo}>
                        {item.dietCategoryName && (
                          <span className={`${styles.categoryIndicator} ${styles[item.dietCategoryName?.toLowerCase()]}`} />
                        )}
                        <span className={styles.itemName}>{item.itemName || '-NA-'}</span>
                      </div>

                      <div className={styles.stepperContainer}>
                        <button
                          type="button"
                          onClick={() => {
                            if (item.qty > 1) {
                              updateItemQuantity(item, item.qty - 1);
                            }
                          }}
                          className={styles.stepperButton}
                          disabled={item.qty <= 1}
                        >
                          -
                        </button>
                        <span className={styles.quantity}>{item.qty}</span>
                        <button
                          type="button"
                          onClick={() => updateItemQuantity(item, item.qty + 1)}
                          className={styles.stepperButton}
                        >
                          +
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(item)}
                        className={styles.deleteButton}
                      >
                        <i className="pi pi-trash" style={{ fontSize: '16px' }} />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      );
    } else {
      const itemsForTab = selectedIncludedItems.filter(
        item => item.itemGroupId === tab.id && item.itemSubGroupId === 0
      );

      return (
        <div key={tab.id} className={styles.tabSection}>
          <div className={styles.tabHeader}>
            <span className={styles.tabTitle}>{tab.group_name}</span>
            <button
              type="button"
              onClick={() => openItemSelection(tab.id, 0, tab.group_name, '')}
              className={styles.addButton}
            >
              ADD
            </button>
          </div>

          {itemsForTab.length > 0 && (
            <div className={styles.itemsContainer}>
              {itemsForTab.map((item, idx) => (
                <div key={idx} className={styles.selectedItemRow}>
                  <div className={styles.itemInfo}>
                    {item.dietCategoryName && (
                      <span className={`${styles.categoryIndicator} ${styles[item.dietCategoryName?.toLowerCase()]}`} />
                    )}
                    <span className={styles.itemName}>{item.itemName || '-NA-'}</span>
                  </div>

                  <div className={styles.stepperContainer}>
                    <button
                      type="button"
                      onClick={() => {
                        if (item.qty > 1) {
                          updateItemQuantity(item, item.qty - 1);
                        }
                      }}
                      className={styles.stepperButton}
                      disabled={item.qty <= 1}
                    >
                      -
                    </button>
                    <span className={styles.quantity}>{item.qty}</span>
                    <button
                      type="button"
                      onClick={() => updateItemQuantity(item, item.qty + 1)}
                      className={styles.stepperButton}
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeItem(item)}
                    className={styles.deleteButton}
                  >
                    <i className="pi pi-trash" style={{ fontSize: '16px' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
  };

  useEffect(() => {
    if (service) {
      const processedIncludedItems = service.included_items?.length > 0
        ? service.included_items.map(item => ({
            ...item,
            itemId: item.itemId || item.id,
            itemName: item.itemName,
            qty: item.qty || 1,
            itemGroupId: item.itemGroupId,
            itemSubGroupId: item.itemSubGroupId || 0,
            dietCategoryName: item.dietCategoryName || null,
          }))
        : [];

      setSelectedIncludedItems(processedIncludedItems);

      const initialData = {
        item_id: service.item_id,
        custom_combo_name: service.custom_combo_name || '',
        meal_id: selectedcategory || '',
        description: service.description || '',
        regular_price: service.regular_price || '',
        discounted_price: service.discounted_price || '',
        included_items: processedIncludedItems,
        gallery: service.gallery || [],
        valid_until: service.valid_until || '',
        event_ids: service.event_ids || [],
      };

      setFormData(initialData);

      if (isEditMode) {
        initialFormDataRef.current = JSON.parse(JSON.stringify(initialData));
        changedFieldsRef.current.clear();
      }
    } else {
      const createData = {
        item_id: null,
        custom_combo_name: '',
        meal_id: selectedcategory || '',
        description: '',
        regular_price: '',
        discounted_price: '',
        included_items: [],
        gallery: [],
        valid_until: '',
        event_ids: [],
      };

      setFormData(createData);
      initialFormDataRef.current = null;
      changedFieldsRef.current.clear();
    }
  }, [service, isEditMode, selectedcategory]);

  // Auto-validate when formData or selectedIncludedItems changes
  useEffect(() => {
    validateForm();
  }, [formData.custom_combo_name, formData.regular_price, selectedIncludedItems, validateForm]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (isEditMode) {
      changedFieldsRef.current.add(field);
    }
  }, [isEditMode]);

  const handlePriceChange = useCallback((field, value) => {
    const cleanedValue = value.replace(/[^\d]/g, '');
    let finalValue = cleanedValue === '' ? '' : cleanedValue.replace(/^0+/, '') || '0';

    if (finalValue && parseInt(finalValue) > 1000000) {
      finalValue = '1000000';
    }

    if (field === 'discounted_price' && finalValue && formData.regular_price) {
      const regularPrice = parseInt(formData.regular_price);
      const discountPrice = parseInt(finalValue);

      if (discountPrice >= regularPrice) {
        alert('Discounted price must be less than regular price');
        return;
      }
    }

    if (field === 'regular_price' && finalValue && formData.discounted_price) {
      const regularPrice = parseInt(finalValue);
      const discountPrice = parseInt(formData.discounted_price);

      if (discountPrice >= regularPrice) {
        alert('Discounted price must be less than regular price');
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: finalValue
    }));

    if (isEditMode) {
      changedFieldsRef.current.add(field);
    }
  }, [formData.regular_price, formData.discounted_price, isEditMode]);

  const formatInIST = (dateString) => {
    if (dateString && typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{2}$/)) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);

    return `${day}/${month}/${year}`;
  };

  const handleDateChange = useCallback((date) => {
    setFormData(prev => ({
      ...prev,
      valid_until: date
    }));
    if (isEditMode) {
      changedFieldsRef.current.add('valid_until');
    }
    setShowDiscountCalendar(false);
  }, [isEditMode]);

  const handleClearDiscount = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      discounted_price: '',
      valid_until: ''
    }));
    if (isEditMode) {
      changedFieldsRef.current.add('discounted_price');
      changedFieldsRef.current.add('valid_until');
    }
    setShowDiscountCalendar(false);
  }, [isEditMode]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (selectedIncludedItems.length === 0) {
      alert('Please add at least one item before submitting.');
      return;
    }

    if (!formData.custom_combo_name || !formData.custom_combo_name.trim()) {
      alert('Please enter a package name.');
      return;
    }

    if (isPackageMode && (!formData.regular_price || formData.regular_price <= 0)) {
      alert('Please enter a valid regular price.');
      return;
    }

    const submitData = {
      custom_combo_name: formData.custom_combo_name.trim(),
      package_id: formData.package_id || null,
      description: formData.description || null,
      included_items: selectedIncludedItems.map(item => ({
        itemName: item.itemName,
        qty: parseInt(item.qty) || 1,
        itemId: item.itemId || null,
        itemGroupId: item.itemGroupId,
        itemSubGroupId: item.itemSubGroupId
      })),
      meal_id: formData.meal_id || selectedcategory,
      event_ids: formData.event_ids || [],
      image_ids: formData.gallery.map(img => img.id).filter(id => id),
    };

    if (isPackageMode) {
      submitData.regular_price = parseFloat(formData.regular_price) || 0;
      submitData.discounted_price = formData.discounted_price ? parseFloat(formData.discounted_price) : null;
      submitData.valid_until = formData.valid_until || null;
    }

    if (isEditMode) {
      submitData.item_id = formData.item_id;
    }

    onSubmit(submitData, isEditMode);

    if (isEditMode) {
      initialFormDataRef.current = JSON.parse(JSON.stringify(formData));
      changedFieldsRef.current.clear();
    }
  }, [formData, selectedIncludedItems, isEditMode, selectedcategory, onSubmit, isPackageMode]);

  const handleImageUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setNewImages(prev => [...prev, ...files]);

      const uploadPromises = files.map(file => uploadImageAndGetId(file));
      await Promise.all(uploadPromises);

      e.target.value = '';
    }
  }, []);

  const uploadImageAndGetId = useCallback(async (file) => {
    try {
      if (file.size > 10 * 1024 * 1024) {
        console.error("File size exceeds the 10MB limit.");
        return null;
      }

      const formDataObj = new FormData();
      formDataObj.append("file", file);

      const response = await addCateringProviderMenuImage(formDataObj);

      if (response && response.file && response.file.id) {
        const imageId = response.file.id;

        setFormData(prev => ({
          ...prev,
          gallery: Array.isArray(prev.gallery)
            ? [...prev.gallery, { id: imageId }]
            : [{ id: imageId }]
        }));

        return imageId;
      } else {
        console.error('Invalid response structure:', response);
        return null;
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setNewImages(prev => prev.filter(f => f !== file));
      return null;
    }
  }, []);

  const removeExistingImage = useCallback((imageId) => {
    setFormData(prev => {
      const newGallery = prev.gallery.filter(image => image.id !== imageId);
      return {
        ...prev,
        gallery: newGallery
      };
    });
    if (isEditMode) {
      changedFieldsRef.current.add('gallery');
    }
  }, [isEditMode]);

  const handleEventSelect = useCallback((event) => {
    const isAlreadySelected = formData.event_ids.includes(event.id);

    if (isAlreadySelected) {
      return;
    }

    if (formData.event_ids.length >= 3) {
      alert('You can only select up to 3 events');
      return;
    }

    setFormData(prev => ({
      ...prev,
      event_ids: [...prev.event_ids, event.id]
    }));

    setSelectedEvents(prev => [...prev, event]);

    if (isEditMode) {
      changedFieldsRef.current.add('event_ids');
    }

    setEventSearch('');
    setShowEventDropdown(false);
  }, [formData.event_ids, isEditMode]);

  const handleRemoveEvent = useCallback((eventId) => {
    setFormData(prev => ({
      ...prev,
      event_ids: prev.event_ids.filter(id => id !== eventId)
    }));

    setSelectedEvents(prev => prev.filter(event => event.id !== eventId));

    if (isEditMode) {
      changedFieldsRef.current.add('event_ids');
    }
  }, [isEditMode]);

  const filteredEvents = events.filter(event =>
    event.name.toLowerCase().includes(eventSearch.toLowerCase()) &&
    !formData.event_ids.includes(event.id)
  );

  return (
    <form onSubmit={handleSubmit} className={isViewMode ? styles.viewserviceCard : styles.serviceCard}>
      <ItemSelectionModal
        isOpen={showItemModal}
        onClose={() => setShowItemModal(false)}
        onConfirm={confirmItemSelection}
        activeTabId={activeTabId}
        activeSubTabId={activeSubTabId}
        activeTabName={activeTabName}
        activeSubTabName={activeSubTabName}
        allAvailableItems={allAvailableItems}
        initialSelectedItems={tempSelectedItems}
      />

      <div className={isViewMode ? styles.viewgapforheadder : styles.gapforheadder}>
        <div className={styles.formHeader}>
          {isEditMode && (
            <div className={styles.headerLeft}>
              <img src={cateringmenuicon || noimage} style={{ width: "20px" }} alt="menu" />
              <span className={styles.cardtitle}>
                {service?.custom_combo_name || "Menu Item"}
              </span>
            </div>
          )}

          {isEditMode && (
            <div className={styles.headerRight}>
              <div className={styles.menuDropdownContainer}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenuDropdown(!showMenuDropdown);
                  }}
                  className={styles.menuButton}
                  title="Menu options"
                >
                  <i className="pi pi-ellipsis-h"></i>
                </button>

                {showMenuDropdown && (
                  <div className={`${styles.menuDropdown}`} onClick={(e) => e.stopPropagation()}>
                    {isViewMode && (
                      <button
                        type="button"
                        onClick={() => {
                          onToggleEdit();
                          setShowMenuDropdown(false);
                        }}
                        className={styles.menuItem}
                      >
                        <i className="pi pi-pencil" style={{ marginRight: '8px' }}></i>
                        Edit
                      </button>
                    )}
                    <div>
                      {deleteButton && (
                        <div className={styles.deleteButtonContainer}>
                          {deleteButton}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Package Name Input for Edit/Create Mode */}
        {(isEditFormMode || isCreateMode) && (
          <div className={styles.headder}>
            <label className={styles.titlename}>
              Package Name <span style={{ color: 'red' }}>*</span>
            </label>
            <div className={styles.inputWithClear}>
              <input
                type="text"
                value={formData.custom_combo_name || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    custom_combo_name: newValue
                  }));
                  if (isEditMode) {
                    changedFieldsRef.current.add('custom_combo_name');
                  }
                }}
                placeholder="Enter package name"
                className={styles.formInput}
                required
                disabled={isEditMode && !isFormEditable}
              />
              {formData.custom_combo_name && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      custom_combo_name: ''
                    }));
                    if (isEditMode) {
                      changedFieldsRef.current.add('custom_combo_name');
                    }
                  }}
                  className={styles.clearButton}
                  disabled={isEditMode && !isFormEditable}
                  title="Clear package name"
                >
                  <i className="pi pi-times"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Package Name Display for View Mode */}
        {isViewMode && (
          <div className={styles.headder}>
            <span className={styles.titlename}>Package Name</span>
            <div className={styles.viewField}>
              {formData.custom_combo_name || "No package name set"}
            </div>
          </div>
        )}
      </div>

      <div className={isViewMode ? styles.viewservicecardinner : styles.servicecardinner}>
        {/* Image Section for View Mode */}
        {isViewMode && (newImages.length > 0 || (service?.gallery && service.gallery.length > 0)) && (
          <div className={styles.viewrightColumn}>
            <div className={styles.uploadimagescontainer}>
              <span className={styles.titlename}>Images</span>
              {service?.gallery && service.gallery.length > 0 && (
                <div className={styles.existingImages}>
                  <div className={styles.imageGrid}>
                    {formData.gallery.map((image) => (
                      <div key={image.id} className={styles.imagewrapper}>
                        <img
                          src={`${config.API_BASE_URL}/gallery/download/${image.id}`}
                          onError={(e) => (e.target.src = noimage)}
                          alt="Service"
                          className={styles.imagesize}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={isViewMode ? styles.viewleftcolumn : styles.leftColumn}>
          {/* Included Items Section */}
          <div className={isViewMode ? styles.viewitemssection : styles.itemssection}>
            <div className={styles.thirdheader}>
              {(isEditFormMode || isCreateMode) && (
                <div className={styles.tabsContainer}>
                  {tabs.map(tab => buildIncludedItemsSection(tab))}
                </div>
              )}

              {isViewMode && (
                <div className={styles.viewItemsContainer}>
                  {selectedIncludedItems.map((item, idx) => (
                    <div key={idx} className={styles.viewItemRow}>
                      <span className={styles.viewItemName}>{item.itemName}</span>
                      <span className={styles.viewItemQty}>Qty: {item.qty}</span>
                    </div>
                  ))}
                  {selectedIncludedItems.length === 0 && (
                    <div className={styles.noItemsMessage}>No items added</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Description Section for View Mode */}
          {isViewMode && (
            <div className={styles.viewsixtheader}>
              <div>
                <span className={styles.titlename}>Description</span>
              </div>
              <span className={styles.viewformTextarea}>{formData.description ? formData.description : "No Description"}</span>
            </div>
          )}

          {/* Pricing Section - Only for Package Mode */}
          {isPackageMode && (
            <div className={isViewMode ? styles.viewpricingsection : styles.pricingsection}>
              <div className={isViewMode ? styles.viewsevenththeader : styles.sevenththeader}>
                <div className={isViewMode ? styles.viewpricedata : styles.pricedata}>
                  <span className={styles.titlename}>Price *</span>
                  {isViewMode ? (
                    <span className={styles.viewpricevalue}>{formData.regular_price}</span>
                  ) : (
                    <div className={styles.inputWithClear}>
                      <input
                        type="text"
                        value={formData.regular_price || ''}
                        onChange={(e) => handlePriceChange('regular_price', e.target.value)}
                        onKeyDown={(e) => {
                          if (!/^\d$/.test(e.key) &&
                            !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        placeholder="0"
                        className={styles.formInput}
                        required
                        disabled={isEditMode && !isFormEditable}
                      />
                      {formData.regular_price && (
                        <button
                          type="button"
                          onClick={() => handlePriceChange('regular_price', '')}
                          className={styles.clearButton}
                          disabled={isEditMode && !isFormEditable}
                          title="Clear value"
                        >
                          <i className="pi pi-times"></i>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className={isViewMode ? styles.viewpricedata : styles.pricedata}>
                  <span className={styles.titlename}>Offer Price</span>
                  <div className={isViewMode ? styles.viewdiscountInputWrapper : styles.discountInputWrapper}>
                    {isViewMode ? (
                      <span className={styles.viewofferpricevalue}>{formData.discounted_price ? formData.discounted_price : "--"}</span>
                    ) : (
                      <div className={styles.inputWithClear}>
                        <input
                          type="text"
                          value={formData.discounted_price || ''}
                          onChange={(e) => handlePriceChange('discounted_price', e.target.value)}
                          onKeyDown={(e) => {
                            if (!/^\d$/.test(e.key) &&
                              !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          placeholder="0"
                          className={styles.formInput}
                          onFocus={() => {
                            if (formData.discounted_price && !formData.valid_until) {
                              setShowDiscountCalendar(true);
                            }
                          }}
                          disabled={isEditMode && !isFormEditable}
                        />
                        {formData.discounted_price && (
                          <button
                            type="button"
                            onClick={handleClearDiscount}
                            className={styles.clearButton}
                            disabled={isEditMode && !isFormEditable}
                            title="Clear discounted price"
                          >
                            <i className="pi pi-times"></i>
                          </button>
                        )}
                      </div>
                    )}
                    
                    {showDiscountCalendar && formData.discounted_price && !formData.valid_until && (
                      <CalendarDropdown
                        onDateSelect={handleDateChange}
                        selectedDate={formData.valid_until ? format(new Date(formData.valid_until), 'dd/MM/yy') : null}
                        onClose={() => setShowDiscountCalendar(false)}
                      />
                    )}

                    {(formData.valid_until && !isViewMode) && (
                      <div className={styles.selectedDiscountDate}>
                        <span>Offer ends {formatInIST(formData.valid_until)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {(isViewMode && formData.valid_until) && (
                  <div className={styles.viewselectedDiscountDate}>
                    <span>Offer ends</span>
                    <span>{formatInIST(formData.valid_until)}</span>
                  </div>
                )}
              </div>

              {/* Description Section for Edit/Create Mode */}
              {!isViewMode && (
                <div className={styles.sixtheader}>
                  <div>
                    <span className={styles.titlename}>Description</span>
                  </div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="What is your speciality about this package or items? ..."
                    className={styles.formTextarea}
                    rows="3"
                    maxLength={50}
                    disabled={isEditMode && !isFormEditable}
                  />
                </div>
              )}
            </div>
          )}

          {/* Non-Package Mode - Just Description */}
          {!isPackageMode && !isViewMode && (
            <div className={styles.sixtheader}>
              <div>
                <span className={styles.titlename}>Description</span>
              </div>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="What is your speciality about this item? ..."
                className={styles.formTextarea}
                rows="3"
                maxLength={50}
                disabled={isEditMode && !isFormEditable}
              />
            </div>
          )}
        </div>

        {/* Image Upload Section for Edit/Create Mode */}
        {!isViewMode && (
          <div className={styles.rightColumn}>
            <div className={styles.uploadimagescontainer}>
              <span className={styles.titlename}>Images</span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={styles.uploadImageBtn}
                disabled={isEditMode && !isFormEditable}
              >
                + Add
              </button>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleImageUpload}
                accept="image/*"
                className={styles.fileInput}
                multiple
                disabled={isEditMode && !isFormEditable}
              />

              {(newImages.length > 0 || formData.gallery.length > 0) && (
                <div className={styles.existingImages}>
                  <span>Images:</span>
                  <div className={styles.imageGrid}>
                    {formData.gallery.map((image) => (
                      <div key={image.id} className={styles.imagewrapper}>
                        <img
                          src={`${config.API_BASE_URL}/gallery/download/${image.id}`}
                          onError={(e) => (e.target.src = noimage)}
                          alt="Service"
                          className={styles.imagesize}
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingImage(image.id)}
                          className={styles.removeImageBtn}
                          disabled={isEditMode && !isFormEditable}
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.cleardata}>
              <span className={styles.cleardatatitle}>Clear</span>
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    item_id: service?.item_id || null,
                    custom_combo_name: '',
                    meal_id: selectedcategory || '',
                    description: '',
                    regular_price: '',
                    discounted_price: '',
                    included_items: [],
                    gallery: [],
                    valid_until: '',
                    event_ids: [],
                  });
                  setSelectedIncludedItems([]);
                  setSelectedEvents([]);
                }}
                className={styles.clearAllButton}
                disabled={isEditMode && !isFormEditable}
                title="Clear all form data"
              >
                <i style={{ color: "red" }} className="pi pi-trash"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Events Selection */}
      {(isEditFormMode || isCreateMode) && (
        <div className={styles.formGroup}>
          <span className={styles.titlename}>Recommended Events</span>

          {formData.event_ids.length < 3 && (
            <CustomDropdown
              searchValue={eventSearch}
              onSearchChange={setEventSearch}
              showDropdown={showEventDropdown}
              onToggleDropdown={setShowEventDropdown}
              options={filteredEvents}
              onSelect={handleEventSelect}
              placeholder="Search events..."
              renderOption={(event) => event.name}
              disabled={isEditMode && !isFormEditable}
              width="100%"
            />
          )}

          {formData.event_ids.length > 0 && (
            <div className={styles.addedeventItemsTagsSection}>
              <div className={styles.tagsContainer}>
                {selectedEvents.map(event => (
                  <div key={event.id} className={styles.itemTag}>
                    <span className={styles.tagContent}>
                      <span className={styles.itemName}>{event.name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveEvent(event.id)}
                      className={styles.removeTagBtn}
                      title="Remove this event"
                      disabled={isEditMode && !isFormEditable}
                    >
                      <i className='pi pi-times'></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isViewMode && selectedEvents.length > 0 && (
        <div className={styles.vieweventags}>
          <span className={styles.titlename}>Recommended Events</span>
          {selectedEvents.map(event => (
            <div key={event.id} className={styles.viewitemTag}>
              <span className={styles.itemName}>{event.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Form Actions */}
      {(isEditFormMode || isCreateMode) && (
        <div className={styles.formActions}>
          <button type="submit" className={styles.submitButton} disabled={!formValid}>
            {isEditFormMode ? "Save Changes" : "Create Package"}
          </button>
        </div>
      )}
    </form>
  );
});

export default ServiceForm;